/**
 * ReassignmentProgress
 * 
 * Displays real-time progress of a reassignment pipeline execution.
 * Shows each stage with status indicators and timing information.
 * 
 * Stages (in order):
 * 1. assignment_rebinding    - Update operator assignments
 * 2. mobile_superseding      - Mark mobile tasks as superseded
 * 3. pack_regeneration       - Regenerate pack snapshots
 * 4. campaign_regeneration   - Regenerate campaign snapshots
 * 5. cycle_relocking         - Append new cycle locks
 */

import { useEffect } from 'react';
import { CheckCircle2, Circle, Loader2, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

// Stage definitions matching backend REASSIGNMENT_STAGES
export const REASSIGNMENT_STAGES = [
  'assignment_rebinding',
  'mobile_superseding',
  'pack_regeneration',
  'campaign_regeneration',
  'cycle_relocking',
] as const;

export type ReassignmentStage = typeof REASSIGNMENT_STAGES[number];

// Human-readable stage names and descriptions
const STAGE_INFO: Record<ReassignmentStage, { label: string; description: string }> = {
  assignment_rebinding: {
    label: 'Rebinding Assignments',
    description: 'Updating store-format-operator mappings',
  },
  mobile_superseding: {
    label: 'Superseding Mobile Tasks',
    description: 'Marking affected mobile assignments as superseded',
  },
  pack_regeneration: {
    label: 'Regenerating Packs',
    description: 'Building new pack snapshots with updated operators',
  },
  campaign_regeneration: {
    label: 'Regenerating Campaigns',
    description: 'Creating new campaign snapshots from updated packs',
  },
  cycle_relocking: {
    label: 'Relocking Cycles',
    description: 'Appending new cycle locks with reassignment reason',
  },
};

export interface StageProgress {
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  failureReason?: string;
  itemsProcessed?: number;
  processedIds?: number[];
}

export type StageProgressMap = Partial<Record<ReassignmentStage, StageProgress>>;

export interface ReassignmentProgressProps {
  /** Current stage progress from API */
  stageProgress: StageProgressMap;
  /** Overall status of the reassignment */
  status: 'previewed' | 'approved' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  /** Error message if failed */
  errorMessage?: string;
  /** Callback when all stages complete */
  onComplete?: () => void;
}

function getStageStatus(stageProgress: StageProgressMap, stage: ReassignmentStage): StageProgress['status'] {
  return stageProgress[stage]?.status ?? 'pending';
}

function StageIcon({ status }: { status: StageProgress['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'in_progress':
      return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    default:
      return <Circle className="h-5 w-5 text-gray-300" />;
  }
}

function formatDuration(startedAt?: string, completedAt?: string): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;
  
  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
  return `${(durationMs / 60000).toFixed(1)}m`;
}

export function ReassignmentProgress({
  stageProgress,
  status,
  errorMessage,
  onComplete,
}: ReassignmentProgressProps) {
  // Calculate overall progress percentage
  const completedCount = REASSIGNMENT_STAGES.filter(
    (stage) => getStageStatus(stageProgress, stage) === 'completed'
  ).length;
  const progressPercent = (completedCount / REASSIGNMENT_STAGES.length) * 100;

  // Trigger onComplete when all stages are done
  useEffect(() => {
    if (status === 'completed' && onComplete) {
      onComplete();
    }
  }, [status, onComplete]);

  // Find the current/failed stage for highlighting
  const currentStage = REASSIGNMENT_STAGES.find(
    (stage) => getStageStatus(stageProgress, stage) === 'in_progress'
  );
  const failedStage = REASSIGNMENT_STAGES.find(
    (stage) => getStageStatus(stageProgress, stage) === 'failed'
  );

  return (
    <div className="space-y-4">
      {/* Overall Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">
            {status === 'completed' ? 'Reassignment Complete' : 
             status === 'failed' ? 'Reassignment Failed' :
             status === 'in_progress' ? 'Executing Pipeline...' :
             'Preparing...'}
          </span>
          <span className="text-muted-foreground">{Math.round(progressPercent)}%</span>
        </div>
        <Progress 
          value={progressPercent} 
          className={cn(
            "h-2",
            status === 'failed' && "bg-red-100 [&>div]:bg-red-500",
            status === 'completed' && "[&>div]:bg-green-500"
          )}
        />
      </div>

      {/* Stage List */}
      <div className="space-y-1">
        {REASSIGNMENT_STAGES.map((stage, index) => {
          const stageStatus = getStageStatus(stageProgress, stage);
          const progress = stageProgress[stage];
          const info = STAGE_INFO[stage];
          const duration = formatDuration(progress?.startedAt, progress?.completedAt);
          const isCurrentOrPast = 
            stageStatus === 'completed' || 
            stageStatus === 'in_progress' || 
            stageStatus === 'failed';

          return (
            <div
              key={stage}
              className={cn(
                "flex items-start gap-3 py-2 px-3 rounded-lg transition-colors",
                stageStatus === 'in_progress' && "bg-blue-50",
                stageStatus === 'failed' && "bg-red-50",
                stageStatus === 'completed' && "bg-green-50/50"
              )}
            >
              {/* Stage Number + Icon */}
              <div className="flex items-center gap-2 min-w-[60px]">
                <span className={cn(
                  "text-xs font-medium w-5 h-5 rounded-full flex items-center justify-center",
                  isCurrentOrPast ? "bg-gray-200 text-gray-700" : "bg-gray-100 text-gray-400"
                )}>
                  {index + 1}
                </span>
                <StageIcon status={stageStatus} />
              </div>

              {/* Stage Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "font-medium text-sm",
                    stageStatus === 'pending' && "text-gray-400",
                    stageStatus === 'in_progress' && "text-blue-700",
                    stageStatus === 'completed' && "text-green-700",
                    stageStatus === 'failed' && "text-red-700"
                  )}>
                    {info.label}
                  </span>
                  {duration && (
                    <span className="text-xs text-muted-foreground">
                      {duration}
                    </span>
                  )}
                  {progress?.itemsProcessed !== undefined && progress.itemsProcessed > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({progress.itemsProcessed} items)
                    </span>
                  )}
                </div>
                <p className={cn(
                  "text-xs",
                  stageStatus === 'pending' ? "text-gray-400" : "text-muted-foreground"
                )}>
                  {stageStatus === 'failed' && progress?.failureReason 
                    ? progress.failureReason 
                    : info.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error Message */}
      {status === 'failed' && errorMessage && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-red-800">Pipeline Failed</p>
            <p className="text-red-700 mt-1">{errorMessage}</p>
            {failedStage && (
              <p className="text-red-600 text-xs mt-2">
                Failed at stage: {STAGE_INFO[failedStage].label}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Success Message */}
      {status === 'completed' && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <div className="text-sm">
            <p className="font-medium text-green-800">All Stages Complete</p>
            <p className="text-green-700">
              Operator reassignment has been successfully applied to all affected entities.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
